import React, {useCallback, useState} from 'react'

import {EntitySelectorMenu} from "./menu/menu"
import EntitySelectorContentHeader from "./content/header"
import EntitySelectorMain from "./main/selector-main"
import {EntitySelectorProvider} from "./context"
import {ENTITY_SELECTOR_MODE_CONFIG} from "./mode-config"
import {EntitySelectorEntityType, EntitySelectorMenuItem, EntitySelectorMode} from "./@const"
import {MentionOption} from "../editor/plugins/mentions/option"

// Order is the sidebar's order. The label doubles as the content column's title.
const menuItems: EntitySelectorMenuItem[] = [
    {id: EntitySelectorEntityType.USER,     icon: 'i5-user',        label: 'User'},
    {id: EntitySelectorEntityType.TASK,     icon: 'i5-task',        label: 'Task'},
    {id: EntitySelectorEntityType.PROJECT,  icon: 'i5-project',     label: 'Project'},
    {id: EntitySelectorEntityType.DOCUMENT, icon: 'i5-task-list',   label: 'Document'},
    {id: EntitySelectorEntityType.MEETING,  icon: 'i5-meeting',     label: 'Meeting'},
]

// A host with no mentions to offer (the modal, which hides the User tab) still
// gets a list rather than an undefined -- and the SAME one every render, so the
// user tab's own memo holds.
const NO_USER_MENTIONS: MentionOption[] = []

type Props = {
    // Which host this is. The one thing the two use cases switch on: it decides
    // the geometry, the root's variant class and whether there is title chrome
    // (`mode-config.ts`), and everything below reads it off the context. Popover
    // by default -- the geometry the selector was born with.
    mode?               : EntitySelectorMode

    userMentions?       : MentionOption[]
    projectId?          : ID
    initialEntityType?  : EntitySelectorEntityType
    onEntitySelected    : {(entity:MentionOption):void}

    // "+ New X" next to a type's sub-tabs. A host that offers it also decides
    // what "new" means for it (a task, a meeting) -- the selector itself only
    // forwards which sidebar tab the click came from.
    onCreateNew?        : {(entityType: EntitySelectorEntityType):void}

    // Hide these tabs (e.g. USER for related items, where a user link IS a
    // task-user and must not be created here). Also removed from Tab cycling.
    //
    // Not part of the mode: which tabs a host offers follows from what it is
    // picking, not from the box it is standing in.
    excludedEntityTypes?: EntitySelectorEntityType[]

    // The sidebar heading and the close control, for a mode that has chrome --
    // the mention popover has neither.
    title?              : string
    onClose?            : {():void}
}

// const debug = "EntitySelector"
const debug = false

export default function EntitySelector(props:Props) {

    const mode      = props.mode ? props.mode : EntitySelectorMode.POPOVER
    const config    = ENTITY_SELECTOR_MODE_CONFIG[mode]

    const visibleMenuItems = props.excludedEntityTypes?.length
        ? menuItems.filter(item => !props.excludedEntityTypes!.includes(item.id))
        : menuItems

    const [entityType, setEntityType]   = useState(props.initialEntityType ? props.initialEntityType : visibleMenuItems[0].id)

    const onChangeMenu = useCallback((menuId:EntitySelectorEntityType)=>{
        setEntityType(menuId)
    }, [])

    const onTab = () => {
        const index = _.findIndex(visibleMenuItems, item=>item.id === entityType)
        const nextMenuItem = visibleMenuItems[index + 1]

        if (nextMenuItem) {
            onChangeMenu(nextMenuItem.id)
        } else {
            onChangeMenu(visibleMenuItems[0].id)
        }
    }

    const onEntitySelected = useCallback((entity:MentionOption)=>{
        debug && console.log(debug, 'onEntitySelected', entity)
        props.onEntitySelected(entity)
    }, [])

    debug && console.log(debug, 'render', mode, entityType)

    const styles = {
        width   : config.width,
        height  : config.height
    }

    const currentMenuItem = _.find(visibleMenuItems, item=>{
        return item.id === entityType
    })

    // Both titles are host chrome sharing one box (see `EntitySelectorMenu`), so
    // they come and go together: the modal has a heading and a close, the
    // mention popover has neither and opens straight on the search.
    const hasHeader = config.hasTitleRow && (!!props.title || !!props.onClose)

    const classes = Classes("gd-entity-selector flex-row-left-top", config.className)

    return (
        <EntitySelectorProvider
            mode                = {mode}
            title               = {props.title}
            projectId           = {props.projectId}
            userMentions        = {props.userMentions ? props.userMentions : NO_USER_MENTIONS}
            onClose             = {props.onClose}
            onEntitySelected    = {onEntitySelected}
            onTab               = {onTab}
            onCreateNew         = {props.onCreateNew}
        >
            <div className  = {classes}
                 style      = {styles}
            >
                <EntitySelectorMenu
                    menuItems           = {visibleMenuItems}
                    currentEntityType   = {entityType}
                    onSelect            = {onChangeMenu}
                />

                <div className="entity-selector-content flex-column-top-stretch flex-1 min-w-0">
                    {hasHeader &&
                        <EntitySelectorContentHeader title={currentMenuItem ? currentMenuItem.label : ''}/>
                    }

                    <EntitySelectorMain currentEntityType={entityType}/>
                </div>
            </div>
        </EntitySelectorProvider>
    )
}
